/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { ResourceUri } from '@ngxp/rest';
import { createEmptyStateResource, createErrorStateResource, createStateResource, reloadStateResourceOnConcurrentModification } from '../resource/resource.util';
import {
  CreateListItemFailureProps,
  CreateListItemSuccessProps,
  ListResourceLoadActions,
  LoadListResourceSuccessProps,
  LoadResourceFailureProps,
  TypedActionCreatorWithProps,
} from './actions';
import { initialListResourceState, ListResourceState } from './state.model';

export class ListResourceReducer {
  public reducer: ActionReducer<any, Action>;

  constructor(
    private readonly actions: ListResourceLoadActions,
    private readonly concurrentModificationAction?: TypedActionCreatorWithProps<{ effectedResourceUri: ResourceUri }>,
  ) {
    this.initReducer();
  }

  private initReducer(): void {
    const baseReducer = createReducer(
      initialListResourceState,
      on(this.actions.loadAction, (): ListResourceState => ({
        listResource: createEmptyStateResource(true),
      })),
      on(
        this.actions.loadListSuccessAction,
        (state: ListResourceState, props: LoadListResourceSuccessProps): ListResourceState => ({
          ...state,
          listResource: createStateResource(props.listResource),
        }),
      ),
      on(
        this.actions.loadFailureAction,
        (state: ListResourceState, props: LoadResourceFailureProps): ListResourceState => ({
          ...state,
          listResource: createErrorStateResource(props.error),
        }),
      ),
      on(
        this.actions.clearAction,
        (): ListResourceState => ({
          listResource: createEmptyStateResource(),
        }),
      ),
      on(
        this.actions.reloadAction,
        (state: ListResourceState): ListResourceState => ({
          ...state,
          listResource: { ...state.listResource, reload: true },
        }),
      ),
      on(
        this.actions.createListItemAction,
        (state: ListResourceState): ListResourceState => ({
          ...state,
          createdListItemResource: createEmptyStateResource(true),
        }),
      ),
      on(
        this.actions.createListItemSuccessAction,
        (state: ListResourceState, props: CreateListItemSuccessProps): ListResourceState => ({
          ...state,
          createdListItemResource: createStateResource(props.resource),
        }),
      ),
      on(
        this.actions.createListItemFailureAction,
        (state: ListResourceState, props: CreateListItemFailureProps): ListResourceState => ({
          ...state,
          createdListItemResource: createErrorStateResource(props.error),
        }),
      ),
    );

    if (this.concurrentModificationAction) {
      const cmReducer = createReducer(
        initialListResourceState,
        on(
          this.concurrentModificationAction,
          (state: ListResourceState, { effectedResourceUri }): ListResourceState => ({
            ...state,
            listResource: reloadStateResourceOnConcurrentModification(state.listResource, effectedResourceUri),
          }),
        ),
      );
      this.reducer = (state: ListResourceState, action: Action) => cmReducer(baseReducer(state, action), action);
    } else {
      this.reducer = baseReducer;
    }
  }
}
